<template>
  <div class="h-full">
    <Line
      :data="chartData"
      :options="chartOptions"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script setup lang="ts">
import { australianCurrency, cumulativeSum } from '@/helpers';
import Project from '@/models/Project';
import { useUserStore } from '@/store/user';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { DateTime } from 'luxon';
import { computed } from 'vue';
import { Line } from 'vue-chartjs';

const emit = defineEmits<{
  hover: [index: number | undefined];
}>();

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const props = defineProps<{
  project: Project;
  monthlyCertifiedCumulative: number[];
  baselineCumulative: number[];
  forecastCumulative: number[];
  extendedChartMonths: DateTime[];
}>();

// Chart configuration
const chartId = 'project-progress-timeline-chart';
const datasetIdKey = 'label';
const plugins = [];
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;

const chartData = computed(() => {
  const labels = props.extendedChartMonths.map((month) => {
    return month.toFormat('MMM-yyyy');
  });

  return {
    labels,
    datasets: [
      {
        label: 'Forecast',
        data: props.forecastCumulative,
        fill: false,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 3,
        pointBackgroundColor: '#3B82F6',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        tension: 0.4,
        order: 2,
      },
      {
        label: 'Actual',
        data: props.monthlyCertifiedCumulative,
        fill: false,
        borderColor: '#F59E0B',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        borderWidth: 3,
        pointBackgroundColor: '#F59E0B',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        tension: 0.4,
        order: 1,
      },
      {
        label: 'Baseline',
        data: props.baselineCumulative,
        fill: false,
        borderColor: '#EF4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 3,
        pointBackgroundColor: '#EF4444',
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        tension: 0.4,
        order: 3,
      },
    ],
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    intersect: false,
    mode: 'index' as const,
  },

  onHover: (event: any, activeElements: any[]) => {
    if(activeElements.length > 0) {
      const index = activeElements[0].index;

      emit('hover', index);
    } else {
      emit('hover', undefined);
    }
  },

  plugins: {
    legend: {
      position: 'top' as const,
      labels: {
        usePointStyle: true,
        padding: 8,
        font: {
          size: 12,
          weight: '600',
        },
      },
    },

    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
      callbacks: {
        label(context: any) {
          return context.dataset.label + ': ' + australianCurrency(context.parsed.y);
        },
      },
    },
  },

  scales: {
    x: {
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },

      ticks: {
        font: {
          size: 11,
        },

        color: '#6B7280',
      },
    },

    y: {
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },

      ticks: {
        font: {
          size: 11,
        },

        color: '#6B7280',
        callback(value: any) {
          return australianCurrency(value);
        },
      },
    },
  },

  elements: {
    point: {
      hoverBackgroundColor: '#ffffff',
    },
  },
}));
</script>

<style scoped></style>
